import React, { useEffect, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { AuthProvider, useAuth } from "../context/AuthContext";
import ProtectedRoute from "../components/ProtectedRoute";
import ErrorBoundary from "../components/ErrorBoundary";
import Login from "../features/auth/Login";
import ExternalProjectView from "../features/external/ExternalProjectView";
import { PageSpinner } from "../components/ui/PageSpinner";
import HoldingPage from "../components/HoldingPage";

const isHoldingHost = typeof window !== 'undefined' &&
  (window.location.hostname === 'www.produhkt.com' || window.location.hostname === 'produhkt.com');

// ── Lazy-loaded routes ───────────────────────────────────────────────────────
const Portal = React.lazy(() => import("../pages/Portal"));
const Dashboard = React.lazy(() => import("../features/dashboard"));
const ProjectsPage = React.lazy(() => import("../features/projects"));
const Admin = React.lazy(() => import("../features/admin"));
const BrandsPage = React.lazy(() => import("../features/brands"));
const OrgSettingsPage = React.lazy(() => import("../features/settings/OrgSettingsPage"));
const AccountSettingsPage = React.lazy(() => import("../features/settings/AccountSettingsPage"));
const DevPortal = React.lazy(() => import("../features/dev/DevPortal"));

// PostHog page view tracker — fires on every route change
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof posthog.capture === 'function') {
      posthog.capture('$pageview', { $current_url: window.location.href });
    }
  }, [location.pathname]);
  return null;
}

function RootRedirect() {
  const { loading, isLoggedIn } = useAuth();
  if (loading) return null;
  return <Navigate to={isLoggedIn ? "/brands" : "/login"} replace />;
}

function useThemeHydration() {
  const theme = localStorage.getItem("khv-theme");
  if (theme === "dark") {
    document.documentElement.setAttribute("data-theme", "dark");
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function LazyRoute({ label, children }) {
  return (
    <ProtectedRoute>
      <ErrorBoundary label={label}>
        <Suspense fallback={<PageSpinner />}>{children}</Suspense>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

export default function App() {
  useThemeHydration();
  return (
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          {isHoldingHost && <Route path="/" element={<HoldingPage />} />}
          <Route path="/login" element={<Login />} />
          <Route path="/portal" element={<LazyRoute label="Portal"><Portal /></LazyRoute>} />
          <Route path="/dashboard" element={<LazyRoute label="Dashboard"><Dashboard /></LazyRoute>} />
          <Route path="/projects" element={<LazyRoute label="Projects"><ProjectsPage /></LazyRoute>} />
          <Route path="/admin" element={<LazyRoute label="Admin"><Admin /></LazyRoute>} />
          <Route path="/brands" element={<LazyRoute label="Brands"><BrandsPage /></LazyRoute>} />
          <Route path="/settings/org" element={<LazyRoute label="Org Settings"><OrgSettingsPage /></LazyRoute>} />
          <Route path="/settings/account" element={<LazyRoute label="Account Settings"><AccountSettingsPage /></LazyRoute>} />
          <Route path="/dev" element={<LazyRoute label="Dev Portal"><DevPortal /></LazyRoute>} />
          <Route path="/external/project/:token" element={<ExternalProjectView />} />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </QueryClientProvider>
  );
}
